// @ts-nocheck
import moment from 'moment';

import swalToast from '../components/Common/swalToast';
import { EMPTY_OBJECT } from '../const/CommonConst';
import { extractAllAttributesFromTemplate } from '../components/Configuration/Template/SectionAndAttribute/commonFunction';

// HDFC hardcoded deployment date attributes
export const HDFC_HARDCODED_DEPLOYMENT_DATE_ATTRIBUTES = ['E1', 'E5', 'E9'];

export const EXPECTED_DEPLOYMENT_DATE_ATTRIBUTE = 'B9_4';
const DEPLOYMENT_DELAY_TAT_1_ATTRIBUTE_NAME = 'Deployment Delay TAT 1';
const DEPLOYMENT_DELAY_TAT_2_ATTRIBUTE_NAME = 'Deployment Delay TAT 2';

const THRESHOLD_ATTRIBUTE_NAME = 'Deployment Communication TAT';

const DEPLOYMENT_APPROVAL_WIP_STATE = 'Deployment Approval WIP';

// HDFC hardcoded PIT completion date attributes
export const HDFC_HARDCODED_PIT_COMPLETION_DATE_ATTRIBUTES = [
  'F1',
  'F6',
  'F11',
];

// Mapping between PIT completion dates and deployment dates
export const PIT_COMPLETION_TO_DEPLOYMENT_MAPPING = {
  F1: 'E1',
  F6: 'E5',
  F11: 'E9',
};

// Mapping between deployment date attributes and their approval WIP date attributes
export const DEPLOYMENT_TO_APPROVAL_WIP_MAPPING = {
  E1: 'E16',
  E5: 'E17',
  E9: 'E18',
};

export const HDFC_HARDCODED_DEPLOYMENT_WORKFLOW_STATES = [
  'Deployment Pending',
  DEPLOYMENT_APPROVAL_WIP_STATE,
];

export const HDFC_HARDCODED_PIT_WORKFLOW_STATES = [
  'PIT Pending',
  'PIT Approval WIP',
];

// Alert threshold constants
const ALERT_THRESHOLDS = {
  FIVE_DAYS: 5,
  SEVEN_DAYS: 7,
  TEN_DAYS: 10,
  SIX_MONTHS: 6, // months
};

/**
 * Creates PIT completion delay alerts based on gap between PIT completion and deployment dates
 * @param {number} monthsDiff - Number of months difference
 * @param {Object} pitCompletionDateAttribute - PIT completion date attribute object
 * @param {Object} deploymentDateAttribute - Deployment date attribute object
 * @param {Array} existingAlerts - Array to store alerts
 * @returns {void}
 */
const createPitCompletionDelayAlert = (
  monthsDiff,
  pitCompletionDateAttribute,
  deploymentDateAttribute,
  existingAlerts
) => {
  let alertMessage = '';
  let alertId = crypto.randomUUID();

  if (monthsDiff >= ALERT_THRESHOLDS.SIX_MONTHS) {
    alertMessage = `PIT completed post 6 months of ${deploymentDateAttribute?.label}`;
  }

  if (alertMessage) {
    const alert = {
      event_id: alertId,
      msg: alertMessage,
      type: 'pit_completion_delay',
      attribute: pitCompletionDateAttribute,
      isAlertFromPitCompletionDelay: true,
    };

    // Check if alert already exists to avoid duplicates
    const existingAlert = existingAlerts.find(
      // eslint-disable-next-line no-shadow
      (alert) => alert?.event_id === alertId
    );
    if (!existingAlert) {
      existingAlerts.push(alert);
    }
  }
};

/**
 * Creates deployment delay alerts based on gap between deployment and expected dates
 * @param {number} daysDiff - Number of days difference
 * @param {Object} deploymentDateAttribute - Deployment date attribute object
 * @param {number} tat1Value - Dynamic threshold value for 5-day alert (TAT 1)
 * @param {number} tat2Value - Dynamic threshold value for 10-day alert (TAT 2)
 * @param {Array} existingAlerts - Array to store alerts
 * @returns {void}
 */
const createDeploymentDelayAlert = (
  daysDiff,
  deploymentDateAttribute,
  tat1Value,
  tat2Value,
  existingAlerts
) => {
  let alertMessage = '';
  let alertId = crypto.randomUUID();

  if (daysDiff > tat2Value) {
    alertMessage = `${deploymentDateAttribute?.label} is delayed by greater than ${tat2Value} days`;
  } else if (daysDiff > tat1Value) {
    alertMessage = `${deploymentDateAttribute?.label} is delayed by greater than ${tat1Value} days`;
  }

  if (alertMessage) {
    const alert = {
      event_id: alertId,
      msg: alertMessage,
      type: 'deployment_delay',
      attribute: deploymentDateAttribute,
      isAlertFromDeploymentDelay: true,
    };

    // Check if alert already exists to avoid duplicates
    const existingAlert = existingAlerts.find(
      // eslint-disable-next-line no-shadow
      (alert) => alert?.event_id === alertId
    );
    if (!existingAlert) {
      existingAlerts.push(alert);
    }
  }
};

/**
 * Creates deployment communication delay alerts based on gap between deployment date and current date
 * @param {number} daysDiff - Number of days difference
 * @param {Object} deploymentDateAttribute - Deployment date attribute object
 * @param {number} thresholdValue - Threshold value for communication delay
 * @param {Array} existingAlerts - Array to store alerts
 * @returns {void}
 */
const createDeploymentCommunicationDelayAlert = (
  daysDiff,
  deploymentDateAttribute,
  thresholdValue,
  existingAlerts
) => {
  let alertMessage = '';
  let alertId = crypto.randomUUID();
  if (daysDiff >= thresholdValue) {
    alertMessage = `Deployment Communication delay is greater than ${thresholdValue} days for ${deploymentDateAttribute?.label}`;
  }

  if (alertMessage) {
    const alert = {
      event_id: alertId,
      msg: alertMessage,
      type: 'deployment_communication_delay',
      attribute: deploymentDateAttribute,
      isAlertFromDeploymentCommunicationDelay: true,
    };

    // Check if alert already exists to avoid duplicates
    const existingAlert = existingAlerts.find(
      // eslint-disable-next-line no-shadow
      (alert) => alert?.event_id === alertId
    );
    if (!existingAlert) {
      existingAlerts.push(alert);
    }
  }
};

/**
 * Checks all deployment dates and creates alerts for deployment delays
 * @param {Object} params - Parameters for checking deployment delays
 * @param {Object} params.formDetails - Template form details containing all attributes
 * @param {Object} params.attributeValues - Current attribute values object
 * @returns {Array} Array of deployment delay alerts
 */
export const checkDeploymentDelayAlerts = ({
  formDetails,
  attributeValues = EMPTY_OBJECT,
}) => {
  const alerts = [];

  // Early return if required data is missing
  if (!formDetails) {
    return alerts;
  }

  // Get all attributes from template
  const allAttributesList = extractAllAttributesFromTemplate(formDetails);
  const expectedDeploymentDateAttribute = allAttributesList?.find(
    (attr) => attr.name === EXPECTED_DEPLOYMENT_DATE_ATTRIBUTE
  );

  if (!expectedDeploymentDateAttribute) {
    return alerts;
  }

  // Get the expected deployment date value
  const expectedDateValue =
    attributeValues?.[expectedDeploymentDateAttribute.id];

  if (!expectedDateValue) {
    return alerts;
  }

  const expectedDate = moment(expectedDateValue);
  if (!expectedDate.isValid()) {
    return alerts;
  }

  // Get dynamic threshold values
  const tat1Attribute = allAttributesList?.find(
    (attr) => attr.name === DEPLOYMENT_DELAY_TAT_1_ATTRIBUTE_NAME
  );
  const tat2Attribute = allAttributesList?.find(
    (attr) => attr.name === DEPLOYMENT_DELAY_TAT_2_ATTRIBUTE_NAME
  );

  const tat1Value = tat1Attribute
    ? (attributeValues?.[tat1Attribute.id] ?? ALERT_THRESHOLDS.FIVE_DAYS)
    : ALERT_THRESHOLDS.FIVE_DAYS;
  const tat2Value = tat2Attribute
    ? (attributeValues?.[tat2Attribute.id] ?? ALERT_THRESHOLDS.TEN_DAYS)
    : ALERT_THRESHOLDS.TEN_DAYS;

  // Check each deployment date attribute
  HDFC_HARDCODED_DEPLOYMENT_DATE_ATTRIBUTES.forEach((attributeName) => {
    const deploymentDateAttribute = allAttributesList?.find(
      (attr) => attr.name === attributeName
    );

    if (!deploymentDateAttribute) {
      return;
    }

    // Get the deployment date value
    const deploymentDateValue = attributeValues?.[deploymentDateAttribute.id];

    if (!deploymentDateValue) {
      return;
    }

    const deploymentDate = moment(deploymentDateValue);
    if (!deploymentDate.isValid()) {
      return;
    }

    // Calculate the difference in days
    if (deploymentDate.isAfter(expectedDate)) {
      const daysDiff = deploymentDate.diff(expectedDate, 'days');

      // Create alerts based on thresholds
      if (daysDiff > tat1Value) {
        createDeploymentDelayAlert(
          daysDiff,
          deploymentDateAttribute,
          tat1Value,
          tat2Value,
          alerts
        );
      }
    }
  });

  return alerts;
};

/**
 * Checks PIT completion dates and creates alerts for delays
 * @param {Object} params - Parameters for checking PIT completion delays
 * @param {Object} params.formDetails - Template form details containing all attributes
 * @param {Object} params.attributeValues - Current attribute values object
 * @returns {Array} Array of PIT completion delay alerts
 */
export const checkPitCompletionDelayAlerts = ({
  formDetails,
  attributeValues = EMPTY_OBJECT,
}) => {
  const alerts = [];

  // Early return if required data is missing
  if (!formDetails) {
    return alerts;
  }

  // Get all attributes from template
  const allAttributesList = extractAllAttributesFromTemplate(formDetails);

  // Check each PIT completion date attribute
  HDFC_HARDCODED_PIT_COMPLETION_DATE_ATTRIBUTES.forEach((pitAttributeName) => {
    const pitCompletionDateAttribute = allAttributesList?.find(
      (attr) => attr.name === pitAttributeName
    );

    if (!pitCompletionDateAttribute) {
      return;
    }

    // Get the corresponding deployment date attribute
    const deploymentAttributeName =
      PIT_COMPLETION_TO_DEPLOYMENT_MAPPING[pitAttributeName];
    const deploymentDateAttribute = allAttributesList?.find(
      (attr) => attr.name === deploymentAttributeName
    );

    if (!deploymentDateAttribute) {
      return;
    }

    // Get the PIT completion date value
    const pitCompletionDateValue =
      attributeValues?.[pitCompletionDateAttribute.id];

    if (!pitCompletionDateValue) {
      return;
    }

    // Get the deployment date value
    const deploymentDateValue = attributeValues?.[deploymentDateAttribute.id];

    if (!deploymentDateValue) {
      return;
    }

    const pitCompletionDate = moment(pitCompletionDateValue);
    const deploymentDate = moment(deploymentDateValue);

    if (!pitCompletionDate.isValid() || !deploymentDate.isValid()) {
      return;
    }

    // Calculate the difference in months
    if (pitCompletionDate.isAfter(deploymentDate)) {
      const monthsDiff = pitCompletionDate.diff(deploymentDate, 'months');

      // Create alerts based on thresholds
      if (monthsDiff >= ALERT_THRESHOLDS.SIX_MONTHS) {
        createPitCompletionDelayAlert(
          monthsDiff,
          pitCompletionDateAttribute,
          deploymentDateAttribute,
          alerts
        );
      }
    }
  });

  return alerts;
};

/**
 * Checks deployment communication delays and creates alerts
 * @param {Object} params - Parameters for checking deployment communication delays
 * @param {Object} params.formDetails - Template form details containing all attributes
 * @param {Object} params.attributeValues - Current attribute values object
 * @returns {Array} Array of deployment communication delay alerts
 */
export const checkDeploymentCommunicationDelayAlerts = ({
  formDetails,
  attributeValues = EMPTY_OBJECT,
  currentState,
}) => {
  const alerts = [];

  // Early return if required data is missing
  if (!formDetails) {
    return alerts;
  }

  // Get all attributes from template
  const allAttributesList = extractAllAttributesFromTemplate(formDetails);

  const thresholdAttribute = allAttributesList?.find(
    (attr) => attr?.name === THRESHOLD_ATTRIBUTE_NAME
  );

  if (!thresholdAttribute) {
    return alerts;
  }

  const thresholdValue =
    attributeValues?.[thresholdAttribute.id] ?? ALERT_THRESHOLDS.SEVEN_DAYS;

  if (!thresholdValue) {
    return alerts;
  }

  // Check each deployment date attribute
  HDFC_HARDCODED_DEPLOYMENT_DATE_ATTRIBUTES.forEach((attributeName) => {
    const deploymentDateAttribute = allAttributesList?.find(
      (attr) => attr.name === attributeName
    );

    if (!deploymentDateAttribute) {
      return;
    }

    // Get the deployment date value
    const deploymentDateValue = attributeValues?.[deploymentDateAttribute.id];

    if (!deploymentDateValue) {
      return;
    }

    const deploymentDate = moment(deploymentDateValue);
    if (!deploymentDate.isValid()) {
      return;
    }

    // Determine reference date based on current state
    let referenceDate;
    if (currentState === DEPLOYMENT_APPROVAL_WIP_STATE) {
      // Use corresponding E16/E17/E18 attribute value instead of current date
      const approvalWipAttributeName =
        DEPLOYMENT_TO_APPROVAL_WIP_MAPPING[attributeName];
      const approvalWipAttribute = allAttributesList?.find(
        (attr) => attr.name === approvalWipAttributeName
      );

      if (!approvalWipAttribute) {
        return; // Skip if approval WIP attribute doesn't exist
      }

      const approvalWipDateValue = attributeValues?.[approvalWipAttribute.id];
      if (!approvalWipDateValue) {
        return; // Skip if approval WIP date value doesn't exist
      }

      referenceDate = moment(approvalWipDateValue);
      if (!referenceDate.isValid()) {
        return; // Skip if approval WIP date is invalid
      }
    } else {
      // Use current date for 'Deployment Pending' or other states
      referenceDate = moment();
    }

    // Calculate the difference in days - only check if deployment date is in the past
    const daysDiff = referenceDate.diff(deploymentDate, 'days');

    // Create alerts based on threshold if date is in the past
    if (daysDiff >= thresholdValue) {
      createDeploymentCommunicationDelayAlert(
        daysDiff,
        deploymentDateAttribute,
        thresholdValue,
        alerts
      );
    }
  });

  return alerts;
};

/**
 * Validates deployment date communication delay against current date and shows warning if needed
 * @param {Object} params - Validation parameters
 * @param {Object} params.attribute - The attribute being validated (should have name property)
 * @param {string|Date} params.newValue - The new date value to validate
 * @param {Object} params.formDetails - Template form details containing all attributes
 * @param {Object} params.attributeValues - Current attribute values object
 * @param {string} params.currentState - Current workflow state
 * @returns {void}
 */
export const validateDeploymentDateCommunication = ({
  attribute,
  newValue,
  formDetails,
  attributeValues = EMPTY_OBJECT,
  currentState = '',
}) => {
  // Early returns if required data is missing
  if (!attribute || !newValue) {
    return;
  }

  // Get all attributes from template
  const allAttributesList = extractAllAttributesFromTemplate(formDetails);

  const thresholdAttribute = allAttributesList?.find(
    (attr) => attr?.name === THRESHOLD_ATTRIBUTE_NAME
  );

  const thresholdValue =
    attributeValues?.[thresholdAttribute?.id] ?? ALERT_THRESHOLDS.SEVEN_DAYS;
  if (!thresholdAttribute) {
    return;
  }

  // Check if this is a deployment date attribute
  if (!HDFC_HARDCODED_DEPLOYMENT_DATE_ATTRIBUTES.includes(attribute?.name)) {
    return;
  }

  const inputDate = moment(newValue);

  if (!inputDate.isValid()) {
    return;
  }

  // Determine reference date based on current state
  let referenceDate;
  if (currentState === DEPLOYMENT_APPROVAL_WIP_STATE) {
    // Use corresponding E16/E17/E18 attribute value instead of current date
    const approvalWipAttributeName =
      DEPLOYMENT_TO_APPROVAL_WIP_MAPPING[attribute?.name];
    const approvalWipAttribute = allAttributesList?.find(
      (attr) => attr.name === approvalWipAttributeName
    );

    if (!approvalWipAttribute) {
      return; // Skip if approval WIP attribute doesn't exist
    }

    const approvalWipDateValue = attributeValues?.[approvalWipAttribute.id];
    if (!approvalWipDateValue) {
      return; // Skip if approval WIP date value doesn't exist
    }

    referenceDate = moment(approvalWipDateValue);
    if (!referenceDate.isValid()) {
      return; // Skip if approval WIP date is invalid
    }
  } else {
    // Use current date for 'Deployment Pending' or other states
    referenceDate = moment();
  }

  // Calculate the difference in days - only check if input date is in the past
  const daysDifference = referenceDate.diff(inputDate, 'days');

  // Show communication delay message only if input date is in the past and difference is 7 days or more
  if (daysDifference >= thresholdValue) {
    swalToast({
      icon: 'warning',
      title: `Deployment Communication delay is greater than ${thresholdValue} days.`,
    });
  }
};

/**
 * Validates deployment date against expected deployment date and shows warning if needed
 * @param {Object} params - Validation parameters
 * @param {Object} params.attribute - The attribute being validated (should have name property)
 * @param {string|Date} params.newValue - The new date value to validate
 * @param {Object} params.formDetails - Template form details containing all attributes
 * @param {Object} params.attributeValues - Current attribute values object
 * @returns {void}
 */
export const validateDeploymentDate = ({
  attribute,
  newValue,
  formDetails,
  attributeValues = EMPTY_OBJECT,
}) => {
  // Early returns if required data is missing
  if (!attribute || !newValue || !formDetails) {
    return;
  }

  // Check if this is a deployment date attribute
  if (!HDFC_HARDCODED_DEPLOYMENT_DATE_ATTRIBUTES.includes(attribute?.name)) {
    return;
  }

  // Get all attributes from template
  const allAttributesList = extractAllAttributesFromTemplate(formDetails);
  const expectedDeploymentDateAttribute = allAttributesList?.find(
    (attr) => attr.name === EXPECTED_DEPLOYMENT_DATE_ATTRIBUTE
  );

  if (!expectedDeploymentDateAttribute) {
    return;
  }

  // Get the expected deployment date value from form values or attribute values
  const expectedDateValue =
    attributeValues?.[expectedDeploymentDateAttribute.id];

  if (!expectedDateValue) {
    return;
  }

  // Perform date validation
  const deploymentDate = moment(newValue);
  const expectedDate = moment(expectedDateValue);

  if (
    deploymentDate.isValid() &&
    expectedDate.isValid() &&
    deploymentDate.isAfter(expectedDate)
  ) {
    const daysDifference = deploymentDate.diff(expectedDate, 'days');
    if (daysDifference > ALERT_THRESHOLDS.FIVE_DAYS) {
      swalToast({
        icon: 'warning',
        title: `The selected date exceeds the ${expectedDeploymentDateAttribute?.label} by ${daysDifference} ${daysDifference === 1 ? 'day' : 'days'}. Higher-level approval is required.`,
      });
    }
  }
};
